/**
 * How a workout is PRINTED — *"Treino B · Peito & Dorsais"* — in one place,
 * because five screens print it: the deck's eyebrow and the draft's group
 * header (`T56`), Today's hero and the plan header (`T57`), the eight
 * "Treino {label}" call sites (`T58`), Retrospectiva (`T65`) and the
 * sidebar's idle line (`T79`). Writing the composition once is what keeps
 * them identical.
 *
 * ── THE NAME IS NEVER DERIVED HERE ─────────────────────────────────────────
 * `planSessionView.name` is the SERVER's arithmetic (`T55`): the two primary
 * muscles the session prescribes the most sets for, resolved in the request's
 * locale, stored at publish and editable through
 * `PUT /me/plan/sessions/:id`. This module composes strings around what the
 * wire sent and computes no name of its own — a name derived on every render
 * cannot be renamed. It is OPTIONAL on the wire and never `null`: absent from
 * a session with no items, and absent from every server older than `T55`,
 * which is why {@link sessionName} falls back to {@link sessionTitle}.
 *
 * ── THE LABEL IS TRANSLATED HERE, AND ONLY HERE ────────────────────────────
 * The engine labels a four-day plan *"Upper A"* / *"Lower B"* and a five-to-
 * six-day plan *"Push"* / *"Pull"* / *"Legs"*, in English
 * (`prescription/split.ts`), and every pt-BR message then wraps the label as
 * "Treino {label}" — so a pt-BR person reads *"Treino Upper A"*. The label
 * is stored display text a person may rename, so it cannot be translated in
 * the database; it can only be translated where it is printed, which is
 * {@link displayLabel}. A label that is not one of the engine's own words —
 * a bare "A", or anything hand-written — is returned untouched.
 *
 * Pure: no hooks, no queries, no fetch. The locale is whatever Paraglide's
 * `getLocale()` answers inside the message functions, as everywhere else.
 */
import type { PlanItemView } from "@repo/contract/training";
import {
  "plan.label_legs" as labelLegs,
  "plan.label_lower" as labelLower,
  "plan.label_pull" as labelPull,
  "plan.label_push" as labelPush,
  "plan.label_upper" as labelUpper,
  "plan.session_name" as sessionNameText,
  "plan.session_title" as sessionTitleText,
} from "@repo/i18n";

/**
 * The engine's own output, exactly: one of five words, optionally followed by
 * the single letter `splitBlueprints` appends when a base repeats. Anchored
 * and case-sensitive on purpose — "upper body" and "Upper/Lower" are things a
 * person wrote, and a person's words are never rewritten.
 */
const ENGINE_LABEL = /^(Upper|Lower|Push|Pull|Legs)( [A-Z])?$/;

const LABEL_WORDS: Readonly<Record<string, () => string>> = {
  Legs: labelLegs,
  Lower: labelLower,
  Pull: labelPull,
  Push: labelPush,
  Upper: labelUpper,
};

/**
 * The label as the person should read it: an engine word translated
 * ("Upper A" → "Superior A"), the letter suffix kept; a bare letter or a
 * hand-written label verbatim.
 */
export function displayLabel(label: string): string {
  const match = ENGINE_LABEL.exec(label);
  const word = match?.[1];
  if (word === undefined) {
    return label;
  }
  const translate = LABEL_WORDS[word];
  if (translate === undefined) {
    return label;
  }
  return `${translate()}${match?.[2] ?? ""}`;
}

/** *"Treino B"* — the existing `plan.session_title` wording, over
 *  {@link displayLabel} rather than over the raw label. */
export function sessionTitle(label: string): string {
  return sessionTitleText({ label: displayLabel(label) });
}

/**
 * The full line — *"Treino B · Peito & Dorsais"* — the stored name from the
 * wire behind the title. {@link sessionTitle} alone when there is no name: a
 * session with no items, or a server older than `T55`.
 */
export function sessionName(label: string, name: string | undefined): string {
  if (name === undefined || name.trim() === "") {
    return sessionTitle(label);
  }
  return sessionNameText({ label: displayLabel(label), name });
}

/**
 * The joiner `generateSessionName` builds the name with — a literal there
 * (`core/model/session-name.ts`) and a literal here, so the two halves of the
 * same string agree without a shared import across the wire.
 */
const NAME_JOINER = " & ";

/** The hero draws three chips and no more. */
const MAX_OTHER_MUSCLES = 3;

/**
 * The muscle chips under the hero's name: the session's DISTINCT primary
 * muscles in first-position order, minus the ones the name already says, at
 * most three.
 *
 * The server joined localized muscle names with `" & "` and the app is
 * reading those same names, in the same locale, out of the same response — so
 * splitting the name back apart is exact. A name the person wrote themselves
 * matches nothing and the full list is shown, which is right: their sentence
 * is not a claim about which muscles the session works.
 *
 * This is the chip line the hero already draws, not a second name.
 */
export function otherMuscles(
  items: readonly Pick<PlanItemView, "exercise" | "position">[],
  name: string | undefined
): string[] {
  const named = new Set(
    (name ?? "")
      .split(NAME_JOINER)
      .map((part) => part.trim())
      .filter((part) => part !== "")
  );
  const ordered = [...items].sort((a, b) => a.position - b.position);
  const chips: string[] = [];
  for (const item of ordered) {
    const muscle = item.exercise.primaryMuscle.name;
    if (
      chips.length < MAX_OTHER_MUSCLES &&
      !(named.has(muscle) || chips.includes(muscle))
    ) {
      chips.push(muscle);
    }
  }
  return chips;
}
